import { FormEvent, KeyboardEvent, useState } from "react";

import { Alert, Button } from "@ezmid/components-library";
import {
  faKey,
  faUserCircle,
  faPaperPlane,
  faSyncAlt,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Navigate } from "react-router";
import { Link } from "react-router-dom";

import { authenticateUser, authSelector } from "./authSlice";
import LoginFormWrapper from "./LoginFormWrapper";
import { Input } from "../../components/inputs/Input";
import { InputGroup } from "../../components/inputs/InputGroup";
import { useAppDispatch } from "../../hooks/store";
import { AllRoutes } from "../route/Route";

export function LoginView(): JSX.Element {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const { isInProgress, isLoggedIn, error } = useSelector(authSelector);

  const [username, setUsername] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [validationError, setValidationError] = useState<string>("");

  if (isLoggedIn) {
    return <Navigate to={AllRoutes.DASHBOARD.path} />;
  }

  // eslint-disable-next-line react-hooks/rules-of-hooks

  const doLogin = async () => {
    if (username && password) {
      setValidationError("");
      dispatch(
        authenticateUser({
          username,
          password,
        })
      );
    } else {
      setValidationError(t("login.error.empty"));
    }
  };

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    doLogin();
  };

  const onClick = () => {
    doLogin();
  };
  const handleKeyPress = (event: KeyboardEvent<HTMLElement>) => {
    if (event.key === "Enter") {
      event.preventDefault();
      doLogin();
    }
  };

  return (
    <LoginFormWrapper>
      <form className="login" onSubmit={onSubmit}>
        <h1 className="text-4xl mb-6">{t("app.auth.login")}</h1>
        {validationError.length > 0 || error?.message ? (
          <Alert variant="error">
            {validationError.length ? validationError : error?.message}
          </Alert>
        ) : (
          <div className="text-gray-400 mb-4">{t("app.auth.pleaseSignIn")}</div>
        )}
        <InputGroup>
          <Input
            autoFocus
            icon={faUserCircle}
            value={username}
            placeholder={t("login.form.username")}
            onChange={(e) => setUsername(e.currentTarget.value)}
            onKeyPress={handleKeyPress}
          />
        </InputGroup>
        <InputGroup>
          <div className="pb-2">
            <Input
              className="mb-4"
              icon={faKey}
              type="password"
              value={password}
              placeholder={t("login.form.password")}
              onChange={(e) => setPassword(e.currentTarget.value)}
              onKeyPress={handleKeyPress}
            />
          </div>
        </InputGroup>
        <Button
          type="submit"
          leftIcon={
            <FontAwesomeIcon
              icon={isInProgress ? faSyncAlt : faPaperPlane}
              spin={isInProgress}
            />
          }
          variant="primary"
          onClick={onClick}
        >
          {t("app.auth.login").toUpperCase()}
        </Button>
      </form>
      <Link
        className="px-0 text-sm text-gray-400 hover:text-blue-500"
        to={AllRoutes.AUTH_RESET_PASSWORD.path}
      >
        {t("app.auth.forgotPassword")}
      </Link>
    </LoginFormWrapper>
  );
}
